// Seller, chaves de API (`spec/24-croqui-alinhamento.md` §5, WEB-56). Igual ao frame
// `screens/seller/chaves-api.tsx` do croqui em 1440.
//
// Foco da tela: a **tabela de chaves**. Quem tem `useQuery`/`useMutation` é esta rota; os
// componentes de `features/api-keys` recebem dado por prop.
//
// Os três frames exportados do croqui (`NovaChave`, `SegredoExibido`, `RevogarChave`) são
// gavetas de AÇÃO, não detalhe de recurso: ficam em estado local, sem URL. Um
// `RouteDrawer` aqui tornaria o segredo endereçável (`spec/24` §1 decisão 5 fala de `$id`).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  API_KEYS_QUERY_KEY,
  type ApiKey,
  apiKeysQueryOptions,
  createApiKey,
  revokeApiKey,
  rotateApiKey,
} from '../api/queries/api-keys.ts';
import { profileQueryOptions } from '../api/queries/overview.ts';
import { useMode } from '../app/mode.tsx';
import { Badge } from '../components/kit/badge.tsx';
import { Btn } from '../components/kit/btn.tsx';
import { Icon } from '../components/kit/icon.tsx';
import { Mono } from '../components/kit/mono.tsx';
import { Notice } from '../components/kit/notice.tsx';
import { PageHeader } from '../components/kit/page-header.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { type ApiKeyRowError, ApiKeyTable } from '../features/api-keys/api-key-table.tsx';
import {
  activeCount,
  isStepUpCancelled,
  keyErrorMessage,
  keysOfMode,
  MAX_ACTIVE_KEYS_PER_MODE,
} from '../features/api-keys/model.ts';
import { NewKeyDrawer } from '../features/api-keys/new-key-drawer.tsx';
import { type RevealedKey, RevealKeyDrawer } from '../features/api-keys/reveal-key-drawer.tsx';
import { RevokeKeyDrawer } from '../features/api-keys/revoke-key-drawer.tsx';

function ApiKeys() {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const keys = useQuery(apiKeysQueryOptions());
  // Mesma query do resto do painel (cache compartilhado): o aviso de verificação depende
  // de `kyc_status`, que só existe em `GET /v1/me`.
  const profile = useQuery(profileQueryOptions(mode));

  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<RevealedKey | undefined>(undefined);
  const [toRevoke, setToRevoke] = useState<ApiKey | undefined>(undefined);
  const [rowError, setRowError] = useState<ApiKeyRowError | undefined>(undefined);
  const [busyKeyId, setBusyKeyId] = useState<string | undefined>(undefined);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [API_KEYS_QUERY_KEY] });
  };

  const create = useMutation({
    // `gcTime: 0` nas duas mutations que devolvem segredo: sem isso o `MutationCache`
    // guardaria `data.key` em claro por mais 5 min depois de a gaveta fechar.
    gcTime: 0,
    mutationFn: async (name: string) => createApiKey({ name, mode }),
    onSuccess: (created) => {
      setCreateOpen(false);
      setRevealed(created);
      void refresh();
    },
  });

  const rotate = useMutation({
    gcTime: 0,
    mutationFn: async (key: ApiKey) => {
      setBusyKeyId(key.id);
      setRowError(undefined);
      return rotateApiKey(key.id);
    },
    onSuccess: (rotated) => {
      setRevealed(rotated);
    },
    onError: (error, key) => {
      if (!isStepUpCancelled(error)) {
        setRowError({ keyId: key.id, message: keyErrorMessage(error, mode) });
      }
    },
    onSettled: async () => {
      setBusyKeyId(undefined);
      await refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: async (key: ApiKey) => {
      setRowError(undefined);
      return revokeApiKey(key.id);
    },
    onSuccess: () => {
      setToRevoke(undefined);
    },
    onError: (error, key) => {
      setToRevoke(undefined);
      if (!isStepUpCancelled(error)) {
        setRowError({ keyId: key.id, message: keyErrorMessage(error, mode) });
      }
    },
    onSettled: refresh,
  });

  /** Fechar a gaveta apaga o segredo do estado E do cache da mutation. */
  const closeReveal = (): void => {
    setRevealed(undefined);
    create.reset();
    rotate.reset();
  };

  const all = keys.data ?? [];
  const rows = keysOfMode(all, mode);
  const active = activeCount(all, mode, new Date());
  const atLimit = active >= MAX_ACTIVE_KEYS_PER_MODE;
  // `undefined` enquanto o perfil carrega: o aviso não pisca antes de a resposta chegar.
  const kycApproved = profile.data === undefined || profile.data.kyc_status === 'approved';

  const openCreate = (): void => {
    create.reset();
    setCreateOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Chaves de API"
        sub="Autenticação da integração via header Authorization"
        path="/v1/me/api-keys"
        size="lg"
        actions={
          <Btn
            kind="primary"
            icon="key"
            // No teto o botão apaga; quem explica é o rodapé do painel ("limite
            // atingido · revogue uma pra criar outra"), como no croqui.
            disabled={atLimit}
            onClick={openCreate}
          >
            Nova chave
          </Btn>
        }
      />

      {/* O toggle live/test do croqui mora no `PageHeader` do kit (`spec/24` §3). Sobra
          o aviso de que toda ação desta tela é sensível. */}
      <div className="flex items-center justify-end gap-3 px-1">
        <span className="inline-flex items-center gap-1.5 text-12 text-faint">
          <Badge>🔒</Badge> exige confirmação por senha ou código
        </span>
      </div>

      {mode === 'live' && !kycApproved && (
        <Notice
          tone="wait"
          icon="shield"
          title="Verificação pendente."
          // `Link`, não `Btn`: é navegação. O desenho é o do `Btn kind="card" small` -
          // `Btn` não tem `asChild` e `<a><button>` seria interativo dentro de interativo.
          action={
            <Link
              to="/app/settings/kyc"
              className="inline-flex h-[34px] min-h-10 shrink-0 items-center gap-2 self-center whitespace-nowrap rounded-[10px] border border-card-border bg-card px-3 text-13 font-medium text-text transition-colors duration-150 ease-out-quint hover:border-tile-border hover:bg-tile sm:min-h-0"
            >
              <Icon name="shield" size={15} />
              Concluir verificação
            </Link>
          }
        >
          Criar chave{' '}
          <Mono size={12} ink="text">
            sk_live_
          </Mono>{' '}
          já exige verificação aprovada: a API recusa com 403 kyc_required. Em teste não há
          restrição.
        </Notice>
      )}

      <Panel>
        <ApiKeyTable
          keys={rows}
          mode={mode}
          activeCount={active}
          isLoading={keys.isPending}
          // Erro só quando não há lista: com `retry: false` e o polling de 30 s, um flake
          // num refetch de fundo trocaria a lista carregada por uma tela de falha.
          error={keys.data === undefined ? keys.error : undefined}
          onRetry={() => {
            void keys.refetch();
          }}
          onRotate={(key) => {
            rotate.mutate(key);
          }}
          onRevoke={setToRevoke}
          onCreate={openCreate}
          {...(busyKeyId === undefined ? {} : { busyKeyId })}
          {...(rowError === undefined ? {} : { rowError })}
        />
      </Panel>

      <NewKeyDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode={mode}
        kycApproved={kycApproved}
        isPending={create.isPending}
        error={create.error}
        onSubmit={(name) => {
          create.mutate(name);
        }}
      />

      <RevealKeyDrawer secret={revealed} onClose={closeReveal} />

      <RevokeKeyDrawer
        apiKey={toRevoke}
        onOpenChange={(open) => {
          if (!open) {
            setToRevoke(undefined);
          }
        }}
        isPending={revoke.isPending}
        onConfirm={(key) => {
          revoke.mutate(key);
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/app/developers/api-keys/')({ component: ApiKeys });
